import { useState, useEffect } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  Heart,
  Baby,
  Stethoscope,
  Syringe,
  Thermometer,
  Phone,
  MapPin,
  Calendar,
  Clock,
  Menu,
  X,
  Star,
  MessageCircle,
  TrendingUp,
  Shield,
  Users,
  Quote,
  ChevronLeft,
  ChevronRight,
  Activity,
  HeartPulse,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

// Clinic Data
const CLINIC_DATA = {
  name: "Orchid Children's Clinic",
  tagline: "Compassionate Pediatric Care for Your Little Ones",
  doctor: "Dr. Mamatha R.",
  doctorTitle: "MBBS, MD Pediatrics, FIPM - Pediatrician & Neonatologist",
  address:
    "Doddakannelli-Chikkanayakana Halli Rd, opp. Someshwara temple, off Sarjapur Main Road, Doddakannelli, Bengaluru, Karnataka 560035",
  phone: "09148324566",
  phone2: "09148304569",
  whatsapp: "09148324566",
  images: {
    doctorPortrait:
      "https://customer-assets.emergentagent.com/job_dr-mamatha-clinic/artifacts/72fzek2k_WhatsApp%20Image%202026-02-16%20at%207.18.13%20PM.jpeg",
    doctorChild:
      "https://customer-assets.emergentagent.com/job_dr-mamatha-clinic/artifacts/gz32b1gm_1.png",
    clinicExterior:
      "https://customer-assets.emergentagent.com/job_dr-mamatha-clinic/artifacts/rmdqt8rv_2.png",
    reception:
      "https://customer-assets.emergentagent.com/job_dr-mamatha-clinic/artifacts/5l8hn4wz_3.png",
    interior:
      "https://customer-assets.emergentagent.com/job_dr-mamatha-clinic/artifacts/0gsck3mx_4.png",
  },
};

const SERVICES = [
  {
    name: "Newborn Care",
    icon: Baby,
    color: "bg-yellow-50",
    borderColor: "border-yellow-200",
    iconColor: "text-yellow-600",
    desc: "Comprehensive care for your newborn",
  },
  {
    name: "Vaccinations & Immunization",
    icon: Syringe,
    color: "bg-blue-50",
    borderColor: "border-blue-200",
    iconColor: "text-blue-600",
    desc: "Complete vaccination schedules",
  },
  {
    name: "Growth & Development",
    icon: TrendingUp,
    color: "bg-green-50",
    borderColor: "border-green-200",
    iconColor: "text-green-600",
    desc: "Track your child's milestones",
  },
  {
    name: "Children Infection Treatment",
    icon: Thermometer,
    color: "bg-red-50",
    borderColor: "border-red-200",
    iconColor: "text-red-600",
    desc: "Expert infection management",
  },
  {
    name: "Bronchial Asthma Treatment",
    icon: Activity,
    color: "bg-pink-50",
    borderColor: "border-pink-200",
    iconColor: "text-pink-600",
    desc: "Specialized respiratory care",
  },
  {
    name: "Infant & Child Nutrition",
    icon: Heart,
    color: "bg-purple-50",
    borderColor: "border-purple-200",
    iconColor: "text-purple-600",
    desc: "Balanced nutrition guidance",
  },
];

const WHY_CHOOSE = [
  {
    icon: HeartPulse,
    title: "Experienced Pediatrician",
    desc: "15+ years of expertise in child healthcare",
  },
  {
    icon: Heart,
    title: "Patient & Compassionate Care",
    desc: "Every child receives gentle, loving attention",
  },
  {
    icon: Activity,
    title: "Holistic Care Approach",
    desc: "Complete physical, emotional & developmental care",
  },
  {
    icon: Users,
    title: "Trusted by Parents",
    desc: "Caring for families since birth",
  },
  {
    icon: Shield,
    title: "Safe Environment",
    desc: "Child-friendly and comfortable clinic",
  },
];

const TESTIMONIALS = [
  {
    text: "Dr Mamta has been treating both my babies from the time of their birth. She is very patient and attentive when we explain our concerns about the babies health. She even explains in detail about the medicines and vaccination to the parents.",
    author: "Happy Parent",
    rating: 5,
  },
  {
    text: "Dr. Mamatha treated my child for viral fever and was very kind, patient, and professional. She explained everything clearly and gave effective treatment. Highly recommend her for pediatric care.",
    author: "Grateful Parent",
    rating: 5,
  },
  {
    text: "We were visiting Dr Mamatha for our daughter since her birth. We found her very caring, understanding and sensible while treating our baby. We will recommend her to others.",
    author: "Satisfied Family",
    rating: 5,
  },
];

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

// Navigation Component
const Navigation = ({ onBookClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Services", href: "#services" },
    { name: "Reviews", href: "#testimonials" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 nav-glass shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-400 rounded-full flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <span className="font-nunito font-bold text-lg md:text-xl text-slate-800">
              Orchid Children's
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-slate-600 hover:text-purple-600 font-medium transition-colors"
              >
                {link.name}
              </a>
            ))}
            <Button
              onClick={onBookClick}
              data-testid="nav-book-appointment-btn"
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-6"
            >
              Book Appointment
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? (
              <X className="w-6 h-6 text-slate-800" />
            ) : (
              <Menu className="w-6 h-6 text-slate-800" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white rounded-2xl shadow-lg mb-4 overflow-hidden"
            >
              <div className="p-4 space-y-3">
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={() => setIsOpen(false)}
                    className="block py-2 px-4 text-slate-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    {link.name}
                  </a>
                ))}
                <Button
                  onClick={() => {
                    setIsOpen(false);
                    onBookClick();
                  }}
                  data-testid="mobile-book-appointment-btn"
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full"
                >
                  Book Appointment
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = ({ onBookClick }) => {
  return (
    <section
      id="home"
      className="relative min-h-screen pt-20 overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #F3E8FF 0%, #FFFFFF 100%)",
      }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 hero-pattern opacity-30"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center lg:text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/80 px-4 py-2 rounded-full shadow-sm mb-6"
            >
              <Heart className="w-4 h-4 text-pink-500" />
              <span className="text-sm font-medium text-slate-600">
                Trusted Pediatric Care
              </span>
            </motion.div>

            <h1 className="font-nunito text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 leading-tight mb-6">
              Compassionate Care for Your{" "}
              <span className="text-purple-600">Little Ones</span>
            </h1>

            <p className="text-lg text-slate-600 mb-8 max-w-xl mx-auto lg:mx-0">
              Providing professional and loving healthcare for newborns,
              infants, and children in Doddakannelli, Bengaluru.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start relative z-10">
              <Button
                onClick={onBookClick}
                data-testid="hero-book-appointment-btn"
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-8 py-6 text-lg btn-primary"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Appointment
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="hero-call-now-btn"
                className="border-purple-300 text-purple-600 hover:bg-purple-50 rounded-full px-8 py-6 text-lg"
              >
                <a href={`tel:${CLINIC_DATA.phone}`}>
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </a>
              </Button>
            </div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-wrap gap-4 mt-10 justify-center lg:justify-start"
            >
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <Shield className="w-5 h-5 text-green-500" />
                <span className="text-sm font-medium text-slate-700">
                  Child-Friendly
                </span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <Star className="w-5 h-5 text-yellow-500" />
                <span className="text-sm font-medium text-slate-700">
                  5-Star Rated
                </span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="relative z-10">
              <div className="absolute -inset-4 bg-gradient-to-br from-purple-200 to-pink-200 rounded-[3rem] transform rotate-3 opacity-50"></div>
              <img
                src={CLINIC_DATA.images.doctorChild}
                alt="Dr. Mamatha with a child patient"
                className="relative z-10 w-full max-w-lg mx-auto rounded-[2.5rem] shadow-2xl"
              />
            </div>
            {/* Floating Elements */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute top-10 -left-4 bg-white p-3 rounded-2xl shadow-lg"
            >
              <Baby className="w-8 h-8 text-pink-400" />
            </motion.div>
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, delay: 1, repeat: Infinity }}
              className="absolute bottom-20 -right-4 bg-white p-3 rounded-2xl shadow-lg"
            >
              <Stethoscope className="w-8 h-8 text-purple-400" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-br from-blue-100 to-purple-100 rounded-[3rem] transform -rotate-3"></div>
            <img
              src={CLINIC_DATA.images.doctorPortrait}
              alt="Dr. Mamatha R - Pediatrician"
              className="relative z-10 w-full max-w-md mx-auto rounded-[2.5rem] shadow-xl"
            />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-purple-600 font-semibold mb-4">
              About Our Doctor
            </span>
            <h2 className="font-nunito text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Meet {CLINIC_DATA.doctor}
            </h2>
            <p className="text-slate-500 text-sm font-medium mb-6">
              {CLINIC_DATA.doctorTitle}
            </p>
            
            <p className="text-slate-600 leading-relaxed mb-4">
              Dr. Mamatha R is a highly experienced Pediatrician and Neonatologist in Bangalore with over 15 years of expertise in neonatal intensive care, pediatric emergencies, and holistic child development. She is widely known for her patient-centered approach, precision in neonatal management, and multidisciplinary care.
            </p>
            
            <p className="text-slate-600 leading-relaxed mb-6">
              Her core expertise includes ELBW and preterm care (as early as 25 weeks), neonatal ventilation, growth and nutrition monitoring, pediatric infectious diseases, and comprehensive immunization programs. Dr. Mamatha is valued for her compassionate communication and evidence-based clinical decision-making.
            </p>

            {/* Education */}
            <div className="mb-6">
              <h3 className="font-nunito font-bold text-lg text-slate-800 mb-3">Education</h3>
              <ul className="space-y-2 text-slate-600 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-1">•</span>
                  MBBS – Karnataka Institute of Medical Sciences, Hubli
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-1">•</span>
                  MD (Pediatrics) – Indira Gandhi Institute of Child Health, Bengaluru
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-1">•</span>
                  Fellowship in Neonatology – Narayana Hrudayalaya, Bengaluru
                </li>
              </ul>
            </div>

            {/* Experience */}
            <div className="mb-6">
              <h3 className="font-nunito font-bold text-lg text-slate-800 mb-3">Experience</h3>
              <ul className="space-y-2 text-slate-600 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-1">•</span>
                  Consultant – Pediatrician & Neonatologist, Apollo Cradle, Electronic City
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-1">•</span>
                  Consultant – Pediatrician & Neonatologist, Apollo Cradle, Koramangala
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-1">•</span>
                  Consultant (NICU) – Best Hospital, Electronic City
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-1">•</span>
                  Consultant (NICU) – Jayashree Multispeciality Hospital
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-1">•</span>
                  Associate Consultant (NICU) – Cloudnine Hospital, Bellandur
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-1">•</span>
                  Associate Consultant (NICU) – Apollo Cradle, Bengaluru
                </li>
              </ul>
            </div>

            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 bg-purple-50 px-4 py-2 rounded-full">
                <Heart className="w-5 h-5 text-purple-500" />
                <span className="text-sm font-medium text-purple-700">
                  15+ Years Experience
                </span>
              </div>
              <div className="flex items-center gap-2 bg-pink-50 px-4 py-2 rounded-full">
                <Baby className="w-5 h-5 text-pink-500" />
                <span className="text-sm font-medium text-pink-700">
                  Neonatal Specialist
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section
      id="services"
      className="py-20"
      style={{
        background: "linear-gradient(180deg, #FFFFFF 0%, #F3E8FF 50%, #FFFFFF 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block text-purple-600 font-semibold mb-4">
            What We Offer
          </span>
          <h2 className="font-nunito text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
            Our Pediatric Services
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Comprehensive healthcare services for your child's every need
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {SERVICES.map((service, index) => (
            <motion.div
              key={service.name}
              variants={fadeInUp}
              data-testid={`service-card-${index}`}
            >
              <Card
                className={`service-card h-full ${service.color} border-2 ${service.borderColor} hover:border-purple-300`}
              >
                <CardContent className="p-6 text-center">
                  <div
                    className={`w-14 h-14 mx-auto mb-4 rounded-2xl bg-white flex items-center justify-center shadow-sm`}
                  >
                    <service.icon className={`w-7 h-7 ${service.iconColor}`} />
                  </div>
                  <h3 className="font-nunito font-bold text-slate-800 mb-2">
                    {service.name}
                  </h3>
                  <p className="text-sm text-slate-600">{service.desc}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseSection = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block text-purple-600 font-semibold mb-4">
            Why Parents Trust Us
          </span>
          <h2 className="font-nunito text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
            Why Choose Orchid Children's Clinic
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6"
        >
          {WHY_CHOOSE.map((item, index) => (
            <motion.div
              key={item.title}
              variants={fadeInUp}
              className="text-center"
              data-testid={`why-choose-card-${index}`}
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center">
                <item.icon className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-nunito font-bold text-slate-800 mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-slate-600">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % TESTIMONIALS.length);
  };

  const prevSlide = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + TESTIMONIALS.length) % TESTIMONIALS.length
    );
  };

  return (
    <section
      id="testimonials"
      className="py-20"
      style={{
        background: "linear-gradient(180deg, #FFFFFF 0%, #F0FDF4 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block text-purple-600 font-semibold mb-4">
            Testimonials
          </span>
          <h2 className="font-nunito text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
            What Parents Say
          </h2>
        </motion.div>

        <div className="relative max-w-4xl mx-auto">
          <Carousel className="w-full" opts={{ loop: true }}>
            <CarouselContent>
              {TESTIMONIALS.map((testimonial, index) => (
                <CarouselItem key={index}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    data-testid={`testimonial-card-${index}`}
                  >
                    <Card className="testimonial-card border-0 shadow-xl">
                      <CardContent className="p-8 md:p-12">
                        <div className="flex justify-center mb-6">
                          <Quote className="w-12 h-12 text-purple-200" />
                        </div>
                        <p className="text-lg md:text-xl text-slate-700 text-center leading-relaxed mb-8">
                          "{testimonial.text}"
                        </p>
                        <div className="flex justify-center mb-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star
                              key={i}
                              className="w-6 h-6 text-yellow-400 fill-yellow-400"
                            />
                          ))}
                        </div>
                        <p className="text-center font-semibold text-slate-800">
                          — {testimonial.author}
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {/* Navigation Buttons */}
          <button
            onClick={prevSlide}
            data-testid="testimonial-prev-btn"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-purple-50 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-purple-600" />
          </button>
          <button
            onClick={nextSlide}
            data-testid="testimonial-next-btn"
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-purple-50 transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-purple-600" />
          </button>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {TESTIMONIALS.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentIndex ? "bg-purple-600" : "bg-purple-200"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  const images = [
    { src: CLINIC_DATA.images.reception, alt: "Clinic Reception" },
    { src: CLINIC_DATA.images.interior, alt: "Clinic Interior" },
    { src: "https://customer-assets.emergentagent.com/job_dr-mamatha-clinic/artifacts/kjx27aq4_5.png", alt: "Clinic Celebration" },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block text-purple-600 font-semibold mb-4">
            Our Space
          </span>
          <h2 className="font-nunito text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
            A Child-Friendly Environment
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {images.map((image, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="overflow-hidden rounded-2xl shadow-lg"
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = ({ onBookClick }) => {
  const [contactForm, setContactForm] = useState({
    name: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Create a formatted message with customer details
    const whatsappMessage = encodeURIComponent(
      `Hello, I would like to contact you.\n\n` +
      `Name: ${contactForm.name}\n` +
      `Phone: ${contactForm.phone}\n` +
      `Message: ${contactForm.message}`
    );

    // Generate WhatsApp link
    const whatsappUrl = `https://wa.me/91${CLINIC_DATA.whatsapp}?text=${whatsappMessage}`;

    // Open WhatsApp chat in new window
    window.open(whatsappUrl, "_blank");

    // Show success message and reset form
    setTimeout(() => {
      toast.success("Opening WhatsApp. Thank you for reaching out!");
      setContactForm({ name: "", phone: "", message: "" });
      setIsSubmitting(false);
    }, 500);
  };

  return (
    <section
      id="contact"
      className="py-20"
      style={{
        background: "linear-gradient(180deg, #FFFFFF 0%, #F3E8FF 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block text-purple-600 font-semibold mb-4">
            Get in Touch
          </span>
          <h2 className="font-nunito text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
            Contact Us
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Call us today to schedule your child's visit
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info & Map */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Contact Cards */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-nunito font-bold text-slate-800 mb-2">
                      Clinic Address
                    </h3>
                    <p className="text-slate-600">{CLINIC_DATA.address}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-pink-600" />
                  </div>
                  <div>
                    <h3 className="font-nunito font-bold text-slate-800 mb-2">
                      Phone Number
                    </h3>
                    <a
                      href={`tel:${CLINIC_DATA.phone}`}
                      className="text-purple-600 hover:text-purple-700 font-semibold text-lg block"
                      data-testid="contact-phone-link"
                    >
                      {CLINIC_DATA.phone}
                    </a>
                    <a
                      href={`tel:${CLINIC_DATA.phone2}`}
                      className="text-purple-600 hover:text-purple-700 font-semibold text-lg"
                      data-testid="contact-phone-link-2"
                    >
                      {CLINIC_DATA.phone2}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-nunito font-bold text-slate-800 mb-2">
                      Clinic Hours
                    </h3>
                    <p className="text-slate-600">
                      Mon - Sat: 5:00 PM - 8:00 PM
                    </p>
                    <p className="text-slate-600">Sun: 12:00 PM - 2:00 PM</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Google Map */}
            <div className="map-container shadow-lg">
              <iframe
                title="Orchid Children's Clinic Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.9!2d77.68!3d12.91!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTLCsDU0JzM2LjAiTiA3N8KwNDAnNDguMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <h3 className="font-nunito text-2xl font-bold text-slate-800 mb-6">
                  Send us a Message
                </h3>
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Your Name
                    </label>
                    <Input
                      type="text"
                      value={contactForm.name}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, name: e.target.value })
                      }
                      placeholder="Enter your name"
                      required
                      data-testid="contact-name-input"
                      className="rounded-xl border-slate-200 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) =>
                        setContactForm({
                          ...contactForm,
                          phone: e.target.value,
                        })
                      }
                      placeholder="Enter your phone number"
                      required
                      data-testid="contact-phone-input"
                      className="rounded-xl border-slate-200 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Message
                    </label>
                    <textarea
                      value={contactForm.message}
                      onChange={(e) =>
                        setContactForm({
                          ...contactForm,
                          message: e.target.value,
                        })
                      }
                      placeholder="How can we help you?"
                      rows={4}
                      data-testid="contact-message-input"
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full py-6 text-lg btn-primary"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Logo & About */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-400 rounded-full flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <span className="font-nunito font-bold text-xl">
                {CLINIC_DATA.name}
              </span>
            </div>
            <p className="text-slate-400">{CLINIC_DATA.tagline}</p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-nunito font-bold text-lg mb-4">Quick Links</h4>
            <div className="space-y-2">
              <a
                href="#home"
                className="block text-slate-400 hover:text-white transition-colors"
              >
                Home
              </a>
              <a
                href="#about"
                className="block text-slate-400 hover:text-white transition-colors"
              >
                About
              </a>
              <a
                href="#services"
                className="block text-slate-400 hover:text-white transition-colors"
              >
                Services
              </a>
              <a
                href="#contact"
                className="block text-slate-400 hover:text-white transition-colors"
              >
                Contact
              </a>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-nunito font-bold text-lg mb-4">Contact</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-slate-400">
                <Phone className="w-4 h-4" />
                <div>
                  <a href={`tel:${CLINIC_DATA.phone}`}>{CLINIC_DATA.phone}</a>
                  <span className="mx-1">/</span>
                  <a href={`tel:${CLINIC_DATA.phone2}`}>{CLINIC_DATA.phone2}</a>
                </div>
              </div>
              <div className="flex items-start gap-2 text-slate-400">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                <span className="text-sm">{CLINIC_DATA.address}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-10 pt-8 text-center">
          <p className="text-slate-500">
            © {new Date().getFullYear()} {CLINIC_DATA.name}. All rights
            reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp Button
const WhatsAppButton = () => {
  const message = encodeURIComponent(
    "Hello, I would like to book an appointment at Orchid Children's Clinic."
  );
  const whatsappUrl = `https://wa.me/91${CLINIC_DATA.whatsapp}?text=${message}`;

  return (
    <motion.a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float-btn"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 1, type: "spring" }}
      className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 transition-colors whatsapp-pulse"
    >
      <MessageCircle className="w-7 h-7 text-white" />
    </motion.a>
  );
};

// Booking Modal
const BookingModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    parentName: "",
    childName: "",
    phone: "",
    date: "",
    time: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Generate time slots based on selected date
  const getTimeSlots = () => {
    if (!formData.date) return [];

    const selectedDate = new Date(formData.date);
    const dayOfWeek = selectedDate.getDay();

    // Sunday = 0
    if (dayOfWeek === 0) {
      // Sunday: 12:00 PM - 2:00 PM (30-min intervals)
      return ["12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM"];
    } else {
      // Mon-Sat: 5:00 PM - 8:00 PM (30-min intervals)
      return ["5:00 PM", "5:30 PM", "6:00 PM", "6:30 PM", "7:00 PM", "7:30 PM", "8:00 PM"];
    }
  };

  const timeSlots = getTimeSlots();

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Create a formatted message with appointment details
    const whatsappMessage = encodeURIComponent(
      `Hello, I would like to book an appointment.\n\n` +
      `Parent Name: ${formData.parentName}\n` +
      `Child Name: ${formData.childName}\n` +
      `Phone: ${formData.phone}\n` +
      `Preferred Date: ${formData.date}\n` +
      `Preferred Time: ${formData.time}\n` +
      `${formData.message ? `Additional Notes: ${formData.message}` : ""}`
    );

    // Generate WhatsApp link
    const whatsappUrl = `https://wa.me/91${CLINIC_DATA.whatsapp}?text=${whatsappMessage}`;

    // Open WhatsApp chat in new window
    window.open(whatsappUrl, "_blank");

    // Show success message and move to submitted state
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
      toast.success("Appointment request submitted!");
    }, 500);
  };

  const handleClose = () => {
    setFormData({
      parentName: "",
      childName: "",
      phone: "",
      date: "",
      time: "",
      message: "",
    });
    setSubmitted(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-md rounded-3xl"
        data-testid="booking-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-nunito text-2xl text-center">
            {submitted ? "Thank You!" : "Book an Appointment"}
          </DialogTitle>
          <DialogDescription className="text-center">
            {submitted
              ? "Our clinic will contact you shortly."
              : "Fill in the details below and we'll get back to you."}
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <Heart className="w-10 h-10 text-green-500" />
            </div>
            <p className="text-slate-600 mb-6">
              We look forward to caring for your child!
            </p>
            <Button
              onClick={handleClose}
              data-testid="booking-close-btn"
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-8"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Parent Name
              </label>
              <Input
                type="text"
                value={formData.parentName}
                onChange={(e) =>
                  setFormData({ ...formData, parentName: e.target.value })
                }
                placeholder="Your name"
                required
                data-testid="booking-parent-name-input"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Child Name
              </label>
              <Input
                type="text"
                value={formData.childName}
                onChange={(e) =>
                  setFormData({ ...formData, childName: e.target.value })
                }
                placeholder="Child's name"
                required
                data-testid="booking-child-name-input"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Phone Number
              </label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder="Your phone number"
                required
                data-testid="booking-phone-input"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Preferred Date
              </label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => {
                  setFormData({ ...formData, date: e.target.value, time: "" });
                }}
                required
                data-testid="booking-date-input"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Preferred Time
              </label>
              <select
                value={formData.time}
                onChange={(e) =>
                  setFormData({ ...formData, time: e.target.value })
                }
                required
                disabled={!formData.date}
                data-testid="booking-time-input"
                className="w-full rounded-xl border border-slate-200 px-4 py-2 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">
                  {!formData.date ? "Select a date first" : "Select a time slot"}
                </option>
                {timeSlots.map((slot) => (
                  <option key={slot} value={slot}>
                    {slot}
                  </option>
                ))}
              </select>
              {formData.date && (
                <p className="text-xs text-slate-500 mt-1">
                  {new Date(formData.date).getDay() === 0
                    ? "Sunday: 12:00 PM - 2:00 PM"
                    : "Mon-Sat: 5:00 PM - 8:00 PM"}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Message (Optional)
              </label>
              <textarea
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                placeholder="Any specific concerns?"
                rows={3}
                data-testid="booking-message-input"
                className="w-full rounded-xl border border-slate-200 px-4 py-2 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
            </div>
            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="booking-submit-btn"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full py-6 btn-primary"
            >
              {isSubmitting ? "Submitting..." : "Book Appointment"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Main App Component
function App() {
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <Navigation onBookClick={() => setIsBookingOpen(true)} />
      <HeroSection onBookClick={() => setIsBookingOpen(true)} />
      <AboutSection />
      <ServicesSection />
      <WhyChooseSection />
      <GallerySection />
      <TestimonialsSection />
      <ContactSection onBookClick={() => setIsBookingOpen(true)} />
      <Footer />
      <WhatsAppButton />
      <BookingModal
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
      />
    </div>
  );
}

export default App;
